import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../../../lib/i18n';
import {
  answerSignature,
  buildVerdictBody,
  castDecisionVerdict,
  forgetVerdictPin,
  isAnswerRecorded,
  mintVerdictId,
  pinVerdictId,
  readVerdictPin,
  writeVerdictPin,
} from '../../../../lib/decisions';
import {
  PROPOSAL_STATE_KEYS,
  canApproveSelected,
  planDeclineAll,
  planProposalVerdicts,
  proposalState,
  proposalsCardSource,
  type ProposalRow,
  type ProposalState,
  type ProposalVerdict,
} from '../../../../lib/proposals-card';
import { Badge, Button, Card, CheckboxGroup, TextButton, Typography } from '../../ui';
import styles from './ProposalsCard.module.css';

/**
 * The proposed tasks a Maestro reply offers, one checkbox each, answered in one press.
 *
 * Not choice chips, for the meeting-approval card's reason: a chip becomes a USER turn and a model cannot
 * cast a verdict. "Approve selected" casts one verdict per OPEN row on `POST /api/decision-verdict` —
 * ticked accepts, unticked refuses ("Decline all" refuses every open row) — with the same per-decision verdict pin `useDecisionAnswer` keeps,
 * so a retry after a lost response is the same vote, not a second one. That hook answers ONE row from its
 * own button; this card answers many from one, so it uses the hook's helpers rather than the hook.
 *
 * Nothing is shown as settled until the service says `ok`; a failed row stays open with its tick kept.
 */
export function ProposalsCard({ rows }: { rows: ProposalRow[] }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [ticked, setTicked] = useState<ReadonlySet<string>>(() => new Set());
  const [collapsed, setCollapsed] = useState(false);
  const [sending, setSending] = useState<'approve' | 'decline' | null>(null);
  const sendingRef = useRef(false);
  // Recorded on THIS screen before the re-read lands — only ever from an `ok` response.
  const [recorded, setRecorded] = useState<ReadonlyMap<string, ProposalState>>(() => new Map());
  const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set());

  const stateOf = (row: ProposalRow): ProposalState => {
    const live = proposalState(row);
    return live === 'open' ? recorded.get(row.decision.id) ?? 'open' : live;
  };
  const openRows = rows.filter((row) => stateOf(row) === 'open');
  const anyOpen = openRows.length > 0;
  const showError = openRows.some((row) => failed.has(row.decision.id));
  const source = proposalsCardSource(rows);

  const setTick = (id: string, on: boolean) =>
    setTicked((current) => {
      const next = new Set(current);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const send = async (control: 'approve' | 'decline', plan: ProposalVerdict[]) => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setSending(control);
    const results = await Promise.all(plan.map(async (verdict) => ({ verdict, ok: await castVerdict(verdict) })));
    setRecorded((current) => {
      const next = new Map(current);
      for (const { verdict, ok } of results) if (ok) next.set(verdict.decisionId, verdict.resolution === 'ACCEPTED' ? 'approved' : 'declined');
      return next;
    });
    setFailed(new Set(results.filter((result) => !result.ok).map((result) => result.verdict.decisionId)));
    sendingRef.current = false;
    setSending(null);
    // By PREFIX: every active `conversation-decisions` entry re-reads under its own key, so the
    // closed-row flags the chat last sent (meeting AND proposals) ride the re-read unchanged.
    await queryClient.invalidateQueries({ queryKey: ['conversation-decisions'] });
    // An approved proposal becomes a task; the task lists should show it without a reload.
    await queryClient.invalidateQueries({ queryKey: ['tasks'] });
  };

  // Collapsing is only for open rows: once nothing is open there is nothing to hide, and no Review link.
  const showRows = !collapsed || !anyOpen;
  const showControls = anyOpen && !collapsed;

  return (
    <Card component="section" className={styles.card} aria-label={t('proposalsCard.title')} data-testid="chat-proposals-card">
      <div className={styles.head}>
        <div className={styles.headText}>
          <Typography variant="h4" color="primary" component="h3">
            {t('proposalsCard.title')}
          </Typography>
          {source && (
            <Typography variant="p2" color="secondary" component="span">
              {t('proposalsCard.fromSource', { source })}
            </Typography>
          )}
        </div>
        {anyOpen && collapsed && (
          <TextButton size="small" onClick={() => setCollapsed(false)} data-testid="chat-proposals-card-review">
            {t('proposalsCard.review')}
          </TextButton>
        )}
      </div>

      {showRows && (
        <ul className={styles.rows}>
          {rows.map((row) => {
            const id = row.decision.id;
            const state = stateOf(row);
            return (
              <li key={id} data-testid={`chat-proposals-card-row-${id}`}>
                {state === 'open' ? (
                  // The DS checkbox, one group per row so a closed row can sit between open ones in
                  // marker order with its status label in place of the box.
                  <CheckboxGroup
                    aria-label={row.decision.question}
                    disabled={sending !== null}
                    value={ticked.has(id) ? [id] : []}
                    onChange={(value) => setTick(id, value.includes(id))}
                    options={[{ value: id, label: <span dir="auto">{row.decision.question}</span> }]}
                  />
                ) : (
                  <div className={styles.closedRow}>
                    <span className={`sym-type-small-medium ${styles.closedTitle} ${state === 'declined' ? styles.declinedTitle : ''}`} dir="auto">
                      {row.decision.question}
                    </span>
                    {/* Only an approval is labelled: a row the owner left unticked just stays quiet. */}
                    {state !== 'declined' && (
                      <Badge variant={state === 'approved' ? 'done' : 'waiting-on-a-reply'} label={t(PROPOSAL_STATE_KEYS[state])} />
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {showControls && (
        <>
          <div className={styles.footer}>
            <Button
              size="small"
              loading={sending === 'approve'}
              disabled={!canApproveSelected(openRows, ticked, failed) || sending !== null}
              onClick={() => void send('approve', planProposalVerdicts(openRows, ticked))}
              data-testid="chat-proposals-card-approve"
            >
              {t('proposalsCard.approveSelected')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              loading={sending === 'decline'}
              disabled={sending !== null}
              onClick={() => void send('decline', planDeclineAll(openRows))}
              data-testid="chat-proposals-card-decline-all"
            >
              {t('proposalsCard.declineAll')}
            </Button>
            <TextButton size="small" disabled={sending !== null} onClick={() => setCollapsed(true)} data-testid="chat-proposals-card-not-now">
              {t('proposalsCard.notNow')}
            </TextButton>
          </div>
          {showError && (
            <Typography variant="p2" color="inherit" component="p" className={styles.error} role="alert">
              {t('proposalsCard.error')}
            </Typography>
          )}
        </>
      )}
    </Card>
  );
}

/** One verdict, pinned per decision so a retry of the same answer replays the same `verdictId`. */
async function castVerdict({ decisionId, resolution }: ProposalVerdict): Promise<boolean> {
  const answer = { resolution };
  const pin = pinVerdictId(readVerdictPin(decisionId), answerSignature(answer), mintVerdictId);
  writeVerdictPin(decisionId, pin);
  // A task-scoped APPROVE: no choice, no words, no plan version — the body is exactly the three ids.
  const result = await castDecisionVerdict(buildVerdictBody({ decisionId, verdictId: pin.verdictId, answer, hold: { decisionId } }));
  const ok = isAnswerRecorded(result);
  if (ok) forgetVerdictPin(decisionId);
  return ok;
}
